import React, {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import { FiArrowLeft } from 'react-icons/fi';
import { HiOutlineMicrophone } from 'react-icons/hi';
import { FaArrowRight } from 'react-icons/fa';
import { MdStop } from 'react-icons/md';
import { BiClipboard } from 'react-icons/bi';
import { GrAttachment } from 'react-icons/gr';

import TextareaAutosize from 'react-textarea-autosize';
import { useSignals } from '@preact/signals-react/runtime';
import { Signal, useComputed } from '@preact/signals-react';
import { motion } from 'motion/react';
import { FileDropzone } from '@/features/ai-agents/shared/file-dropzone';
import { ChatImageTile } from '@/features/ai-agents/components/chat-image-tile';
import {
  AddContextButton,
  ChatContextualTile,
} from '@/features/ai-agents/components/chat-contextual-tile';
import { useChatFilesUpload } from '@/features/ai-agents/shared/use-chat-files-upload';
import {
  AiChatMessage,
  chatNow,
  ContextualAttachment,
  ImageData,
} from '@/features/ai-agents/shared/models';
import { ToolWhiteboard } from '@/features/ai-agents/components/tool-whiteboard';
import {
  ChatVideoUpload,
  ChatVideoUploadHandle,
} from '@/features/ai-agents/components/chat-video-upload';
import { EditorContext } from '@/features/editor/context/editor-context';

// chat-input.tsx

export interface ChatInputProps {
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  maxRows?: number;
  onSubmit?: (msg: AiChatMessage) => void;
  onAbort?: () => void;
  showDropZone?: boolean;

  enableFilesUpload?: boolean;
  enableWhiteboard?: boolean;
  enableExamples?: boolean;
  enableVoiceInput?: boolean;

  contextualAttachments?: Signal<ContextualAttachment[]>;
}

export interface ChatInputHandle {
  setPrompt: (value: string) => void;
  send: () => void;
  openDropzone: () => void;
  openWhiteboard: () => void;
  openVideoUpload: () => void;
}

export const ChatInput = React.forwardRef<ChatInputHandle, ChatInputProps>(
  (
    {
      disabled,
      loading,
      placeholder,
      maxRows,
      onSubmit,
      onAbort,
      showDropZone,
      contextualAttachments,
      enableFilesUpload = true,
      enableWhiteboard = true,
      enableExamples = true,
      enableVoiceInput = true,
    },
    ref,
  ) => {
    useSignals();
    const [prompt, setPrompt] = React.useState(``);
    const [isWaitingForSelection, setIsWaitingForSelection] = useState(false);

    const {
      devicePreview: { aiEditSearch, aiEditWaiting },
    } = useContext(EditorContext);

    const {
      filesList,
      dropzoneProps,
      uploadedImages,
      uploadedFilesRef,
      isUploading,
      onDelete,
      reachMaxFiles,
      clearFiles,
      addFileToUploadQueue,
    } = useChatFilesUpload();
    const [isWhiteboardOpen, setIsWhiteboardOpen] = React.useState(false);
    const videoUploadRef = React.useRef<ChatVideoUploadHandle>(null);

    const isLoading = useMemo(
      () => loading || isUploading,
      [loading, isUploading],
    );

    const onSend = useCallback(() => {
      if (!isLoading && !!prompt) {
        onSubmit?.({
          role: `user`,
          content: prompt,
          created_at: chatNow(),
          images: flattenImages(uploadedImages),
          filesToUpload: uploadedFilesRef,
          contextualAttachments:
            contextualAttachments?.value?.length > 0
              ? contextualAttachments?.value
              : undefined,
        });
        setPrompt(``);
        clearFiles();
      }
    }, [
      isLoading,
      prompt,
      onSubmit,
      uploadedImages,
      uploadedFilesRef,
      clearFiles,
    ]);

    const onContextualAttachmentDelete = useCallback(
      (ids: string | string[]) => {
        const idsArray = Array.isArray(ids) ? ids : [ids];
        contextualAttachments.value = contextualAttachments.value.filter(
          (att) => !idsArray.includes(att.id),
        );
      },
      [],
    );

    const onWhiteboardClick = useCallback(() => {
      setIsWhiteboardOpen(true);
    }, []);

    const onStop = useCallback(() => {
      onAbort?.();
    }, [onAbort]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === `Enter` && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
    };

    const placeholderText = useMemo(() => {
      if (disabled) return `Select an agent to start chatting...`;
      if (placeholder) return placeholder;
      return `Add a custom block using Visually AI`;
    }, [placeholder, disabled]);

    const onUploadSketch = (file: File) => {
      addFileToUploadQueue(file);
      setPrompt(
        `Use this wireframe as a layout reference when building the widget.
  • Match structure & hierarchy exactly.
  •Replicate spacing & relationships between elements.
  •Ignore exact visual styles unless specified.
  •Make it responsive: match the layout for both mobile and desktop unless told otherwise.`,
      );
    };

    useEffect(() => {
      const isVideo =
        uploadedFilesRef.some((f) => f.type.startsWith(`video/`)) ||
        filesList.some((f) => f.type?.startsWith(`video/`));

      if (isVideo && prompt?.length === 0) {
        setPrompt(`Generate a block that faithfully replicates the design of the uploaded images.
Match the layout, styles, color palette, typography, and overall visual hierarchy as closely as possible.
Ensure the content structure and tone mirror the reference,
while keeping proportions and spacing consistent for a polished and cohesive look.
The images are not to be used as assets. they are frames of a video showing different states of a ui component / interaction with a ui component.`);
      } else if (filesList?.length === 1 && prompt?.length === 0) {
        setPrompt(`Generate a block that faithfully replicates the design of the uploaded image.
Match the layout, styles, color palette, typography, and overall visual hierarchy as closely as possible.
Ensure the content structure and tone mirror the reference,
while keeping proportions and spacing consistent for a polished and cohesive look.`);
      }
    }, [filesList, uploadedFilesRef]);

    useImperativeHandle(
      ref,
      () => ({
        setPrompt,
        send: onSend,
        openDropzone: () => {
          if (!reachMaxFiles && enableFilesUpload) {
            dropzoneProps.open?.();
          }
        },
        openWhiteboard: onWhiteboardClick,
        openVideoUpload: () => videoUploadRef.current?.open(),
      }),
      [
        setPrompt,
        onSend,
        dropzoneProps.open,
        reachMaxFiles,
        enableFilesUpload,
        onWhiteboardClick,
      ],
    );

    const groupedAttachments = useComputed(() => {
      const attachments = contextualAttachments?.value || [];
      const processedIds = new Set<string>();
      const result: {
        displayAtt: ContextualAttachment;
        idsToDelete: string[];
      }[] = [];

      attachments.forEach((att) => {
        if (processedIds.has(att.id)) return;

        const isCss = att.id.endsWith(`-css`);
        const baseId = isCss ? att.id.slice(0, -4) : att.id;
        const companionId = isCss ? baseId : `${baseId}-css`;

        const companion = attachments.find((a) => a.id === companionId);
        const idsToDelete = [att.id];

        if (companion) {
          idsToDelete.push(companion.id);
          processedIds.add(companion.id);
        }
        const displayAtt = isCss && companion ? companion : att;
        processedIds.add(att.id);

        result.push({ displayAtt, idsToDelete });
      });

      return result;
    });

    const onAddNew = () => {
      setIsWaitingForSelection(true);
      aiEditSearch();
    };

    const onCancelSelection = () => {
      setIsWaitingForSelection(false);
      const htmlSelectors =
        contextualAttachments?.value
          ?.filter((att) => att.type.includes(`html`))
          .map((att) => att.id) || [];
      aiEditWaiting(htmlSelectors);
    };

    useEffect(() => {
      const handleSectionAdd = () => {
        setIsWaitingForSelection(false);
      };

      window.addEventListener(`aiSectionAdd`, handleSectionAdd);
      return () => {
        window.removeEventListener(`aiSectionAdd`, handleSectionAdd);
      };
    }, []);

    return (
      <Wrapper disabled={disabled}>
        {groupedAttachments.value.length > 0 && (
          <ContextualAttachments>
            {groupedAttachments.value.map(({ displayAtt, idsToDelete }) => (
              <ChatContextualTile
                key={displayAtt.id}
                attachment={displayAtt}
                canDelete
                onDelete={() => onContextualAttachmentDelete(idsToDelete)}
              />
            ))}
            <AddContextButton
              layout
              as={motion.div}
              onClick={isWaitingForSelection ? undefined : onAddNew}
              transition={{ type: `spring`, stiffness: 400, damping: 30 }}
            >
              <InstructionText>
                {isWaitingForSelection && <FiArrowLeft />}
                {isWaitingForSelection ? `Hover & select component` : `+`}
                {isWaitingForSelection && (
                  <CloseButton onClick={onCancelSelection}>✕</CloseButton>
                )}
              </InstructionText>
            </AddContextButton>
          </ContextualAttachments>
        )}
        {filesList?.length > 0 && (
          <Files>
            {filesList.map((file) => (
              <ChatImageTile
                key={file.url}
                imageUrl={file.url}
                loading={file.isUploading}
                hideImage={file.hideImage}
                canZoom
                canDelete
                onDelete={onDelete}
              />
            ))}
          </Files>
        )}
        <TransparentTextArea
          minRows={1}
          maxRows={maxRows ?? 12}
          placeholder={placeholderText}
          onKeyDown={handleKeyDown}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
        <Tools>
          <div className="left">
            {enableFilesUpload && (
              <>
                <GrAttachment
                  size={16}
                  onClick={dropzoneProps.open}
                  style={{
                    pointerEvents: reachMaxFiles ? `none` : `auto`,
                    opacity: reachMaxFiles ? 0.5 : 1,
                  }}
                />
                <ChatVideoUpload
                  ref={videoUploadRef}
                  reachMaxFiles={reachMaxFiles}
                  onUpload={addFileToUploadQueue}
                />
              </>
            )}
            {enableWhiteboard && (
              <BiClipboard
                size={17}
                onClick={onWhiteboardClick}
                style={{
                  pointerEvents: reachMaxFiles ? `none` : `auto`,
                  opacity: reachMaxFiles ? 0.5 : 1,
                }}
              />
            )}
          </div>
          <div className="right">
            {enableExamples ? <Examples>Examples</Examples> : <div />}
            {enableVoiceInput ? (
              <HiOutlineMicrophone size={18} color="#a6afb8" />
            ) : (
              <div />
            )}
            <SendButton onClick={isLoading ? onStop : onSend}>
              {!isLoading && <FaArrowRight size={12} />}
              {isLoading && <MdStop size={15} />}
            </SendButton>
          </div>
        </Tools>
        <FileDropzone overlayVisible={showDropZone} {...dropzoneProps} />
        {isWhiteboardOpen && (
          <ToolWhiteboard
            isWhiteboardOpen={isWhiteboardOpen}
            setIsWhiteboardOpen={setIsWhiteboardOpen}
            onUploadSketch={onUploadSketch}
          />
        )}
      </Wrapper>
    );
  },
);
const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  border-radius: 3rem;
  box-shadow: 0 3px 10.5px 0 rgba(0, 0, 0, 0),
    0 8.5px 10.5px 0 rgba(151, 210, 206, 0.22),
    0 0.5px 0.5px 0 rgba(0, 0, 0, 0.15);
  border: solid 1px #e7eaed;
  background-color: #fff;
  padding: 2rem 2rem;
  gap: 1rem;
  overflow: hidden;

  &&[disabled] {
    background: #f6f5f4;

    && * {
      pointer-events: none !important;
    }
  }
`;

const Files = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 1.5rem;
`;

const ContextualAttachments = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Tools = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  row-gap: 2rem;
  column-gap: 0;

  && > * {
    cursor: pointer;
    user-select: none;
  }

  && .left {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 1.5rem;
  }

  && .right {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: 1.5rem;
  }
`;

const TransparentTextArea = styled(TextareaAutosize)`
  && {
    height: 100%;
    width: 100%;
    background: transparent;
    border: none;
    appearance: none;
    outline: none;
    font-family: Eesti, sans-serif;
    font-size: 1.5rem;
    resize: none;
  }
`;

const Examples = styled.div`
  color: #a6afb8;
  text-decoration: underline;
  font-family: Eesti, sans-serif;
  font-size: 1.4rem;
`;

const SendButton = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  background: black;
  color: white;
  border-radius: 100%;
  height: 3.2rem;
  width: 3.2rem;
  border: none;

  cursor: pointer;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 1.8rem;
  height: 1.8rem;
  cursor: pointer;
  color: rgba(217, 70, 166, 0.46);
  font-size: 1.2rem;
  font-weight: bold;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.2s;

  &:hover {
    background: rgba(217, 70, 166, 0.1);
  }
`;

const InstructionText = styled.span`
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const flattenImages = (uploadedImages: Array<string | string[]>) =>
  uploadedImages?.flatMap((item) => {
    if (Array.isArray(item)) {
      return item.map((url) => ({ url, isFrame: true } as ImageData));
    }
    return [{ url: item as string } as ImageData];
  });
